import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';
import { cragReportsService } from '../../services/crag-reports';
import { cn } from '../../lib/utils';
import { CRAG_REPORT_CATEGORIES, type CragReportCategory } from '../../types/crag-report';
import { climbTitle } from '../../utils/climb-title';
import type { Climb } from '../../types/climb';

/** Radix Select has no empty value, so "the crag itself" carries a token. */
const NO_CLIMB = '__crag__';

/** Matches CragReport::MAX_DESCRIPTION — the server rejects anything longer. */
const MAX_DESCRIPTION = 2000;

interface CragReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cragSlug: string;
  cragName: string;
  /** Every route of the crag, so a report can name one. */
  climbs: Climb[];
  /** Preselected when the FAB was pressed with a line lit on the topo. */
  defaultClimbId?: string | null;
  /** Fired after a report is filed, so the page can pick it up. */
  onCreated?: () => void;
}

/**
 * Filing a report on a crag.
 *
 * The route is optional and defaults to whichever line is lit: half of what
 * goes wrong outdoors belongs to the place (the parking, the path, a nesting
 * ban) rather than to any one line, and forcing a route onto those reports
 * would file them under a wall nobody is asking about.
 *
 * Nothing here says the report is published — it is not. It goes into the
 * crag's queue as a draft, and the confirmation says exactly that.
 */
export function CragReportDialog({
  open,
  onOpenChange,
  cragSlug,
  cragName,
  climbs,
  defaultClimbId,
  onCreated,
}: CragReportDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [category, setCategory] = useState<CragReportCategory>('bolt');
  const [description, setDescription] = useState('');
  const [climbId, setClimbId] = useState<string>(NO_CLIMB);
  const [climbPickerOpen, setClimbPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Opening the dialog is a fresh report, on whichever line is lit right now.
  useEffect(() => {
    if (!open) return;
    setCategory('bolt');
    setDescription('');
    setClimbId(defaultClimbId ?? NO_CLIMB);
  }, [open, defaultClimbId]);

  const sortedClimbs = useMemo(
    () => [...climbs].sort((a, b) => climbTitle(a).localeCompare(climbTitle(b))),
    [climbs]
  );

  const selectedClimb = sortedClimbs.find((climb) => climb.id === climbId) ?? null;

  const handleSubmit = async () => {
    if (!description.trim()) return;

    setSubmitting(true);
    try {
      await cragReportsService.create(cragSlug, {
        description: description.trim(),
        category,
        climb_id: climbId === NO_CLIMB ? null : climbId,
      });

      // The one thing the reporter has to leave with: it was received, and it
      // is not live yet.
      toast({
        title: t('cragReports.createdTitle'),
        description: t('cragReports.createdDescription'),
      });
      onOpenChange(false);
      onCreated?.();
    } catch (error) {
      toast({
        title: t('cragReports.createFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader className="text-left">
          <DialogTitle>{t('cragReports.newTitle')}</DialogTitle>
          <DialogDescription>{t('cragReports.newDescription', { name: cragName })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="crag-report-category">{t('cragReports.categoryLabel')}</Label>
            <Select value={category} onValueChange={(value) => setCategory(value as CragReportCategory)}>
              <SelectTrigger id="crag-report-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRAG_REPORT_CATEGORIES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`cragReports.categories.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="crag-report-climb">{t('cragReports.climbLabel')}</Label>
            {/* Searchable rather than a plain list: a crag runs to hundreds of
                lines, and the reporter already knows the name of the one they
                are standing under. */}
            <Popover open={climbPickerOpen} onOpenChange={setClimbPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="crag-report-climb"
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={climbPickerOpen}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {selectedClimb ? selectedClimb.name : t('cragReports.noClimb')}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder={t('cragReports.searchClimb')} />
                  <CommandList>
                    <CommandEmpty>{t('cragReports.noClimbFound')}</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value={t('cragReports.noClimb')}
                        onSelect={() => {
                          setClimbId(NO_CLIMB);
                          setClimbPickerOpen(false);
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', climbId === NO_CLIMB ? 'opacity-100' : 'opacity-0')} />
                        {t('cragReports.noClimb')}
                      </CommandItem>
                      {sortedClimbs.map((climb) => (
                        <CommandItem
                          key={climb.id}
                          value={climbTitle(climb)}
                          onSelect={() => {
                            setClimbId(climb.id);
                            setClimbPickerOpen(false);
                          }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', climbId === climb.id ? 'opacity-100' : 'opacity-0')} />
                          <span className="truncate">{climb.name}</span>
                          {climb.difficulty && (
                            <span className="ml-auto pl-2 text-xs text-muted-foreground">{climb.difficulty}</span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">{t('cragReports.climbHint')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="crag-report-description">{t('cragReports.descriptionLabel')}</Label>
            <Textarea
              id="crag-report-description"
              value={description}
              onChange={(event) => setDescription(event.target.value.slice(0, MAX_DESCRIPTION))}
              placeholder={t('cragReports.descriptionPlaceholder')}
              rows={5}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !description.trim()}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('cragReports.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
